const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
] as const;

const GOOGLE_ADS_CLICK_KEYS = ['gclid', 'gclsrc', 'gbraid', 'wbraid'] as const;

const GOOGLE_ADS_CONTEXT_KEYS = [
  'campaignid',
  'campaignId',
  'campaign_id',
  'utm_campaign_id',
  'adgroupid',
  'adGroupId',
  'adgroupId',
  'adgroup_id',
  'ad_group_id',
  'utm_adgroup',
  'utm_adgroup_id',
  'utm_ad_group',
  'utm_ad_group_id',
  'adid',
  'adId',
  'ad_id',
  'utm_ad',
  'utm_ad_id',
  'creative',
  'utm_creative',
  'keyword',
  'keywords',
  'keywordId',
  'keyword_id',
  'utm_keyword',
  'criterionid',
  'criterionId',
  'criterion_id',
  'matchtype',
  'network',
  'device',
  'gg_ad_id',
  'gg_matchtype',
  'gg_network',
  'gg_device',
  'placement',
  'target',
  'targetid',
  'adposition',
  'loc_interest_ms',
  'loc_physical_ms',
] as const;

const CHANNEL_SOURCE_KEYS = ['channelSource', 'channel_source', 'source'] as const;
const ATTRIBUTION_KEYS = [
  ...CHANNEL_SOURCE_KEYS,
  ...UTM_KEYS,
  ...GOOGLE_ADS_CLICK_KEYS,
  ...GOOGLE_ADS_CONTEXT_KEYS,
] as const;

const UTM_STORAGE_KEY = 'utm_params';
const ATTRIBUTION_STORAGE_KEY = 'google_ads_attribution';

type UtmKey = (typeof UTM_KEYS)[number];
type GoogleAdsClickKey = (typeof GOOGLE_ADS_CLICK_KEYS)[number];
type GoogleAdsContextKey = (typeof GOOGLE_ADS_CONTEXT_KEYS)[number];
type ChannelSourceKey = (typeof CHANNEL_SOURCE_KEYS)[number];
type AttributionKey = UtmKey | GoogleAdsClickKey | GoogleAdsContextKey | ChannelSourceKey;

export type AttributionParams = Partial<Record<AttributionKey, string>>;

function getCombinedQueryString(): string {
  const searchFromQuery = window.location.search;
  const hashPart = window.location.hash.split('?')[1] || '';

  if (searchFromQuery) return searchFromQuery + (hashPart ? `&${hashPart}` : '');
  if (hashPart) return `?${hashPart}`;
  return '';
}

const normalizeParamValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
};

function getStorage(type: 'local' | 'session'): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return type === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readStorageObjectFrom(
  storage: Storage | null,
  storageKey: string,
): Record<string, string> {
  if (!storage) return {};

  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function readStorageObject(storageKey: string): Record<string, string> {
  return {
    ...readStorageObjectFrom(getStorage('session'), storageKey),
    ...readStorageObjectFrom(getStorage('local'), storageKey),
  };
}

function writeStorageObject(storageKey: string, value: Record<string, string>): void {
  const serialized = JSON.stringify(value);

  [getStorage('local'), getStorage('session')].forEach((storage) => {
    if (!storage) return;
    try {
      storage.setItem(storageKey, serialized);
    } catch {
      // Ignore storage write failures in restricted browsing modes.
    }
  });
}

function pickParams(
  params: AttributionParams,
  keys: readonly AttributionKey[],
): Record<string, string> {
  const picked: Record<string, string> = {};

  keys.forEach((key) => {
    const value = normalizeParamValue(params[key]);
    if (value) {
      picked[key] = value;
    }
  });

  return picked;
}

function pickUtmParams(params: AttributionParams): Record<string, string> {
  return pickParams(params, UTM_KEYS);
}

function pickGoogleAdsClickParams(params: AttributionParams): Record<string, string> {
  return pickParams(params, GOOGLE_ADS_CLICK_KEYS);
}

function pickGoogleAdsContextParams(params: AttributionParams): Record<string, string> {
  return pickParams(params, GOOGLE_ADS_CONTEXT_KEYS);
}

function getAttributionChannelSourceFrom(params: AttributionParams): string | undefined {
  return (
    normalizeParamValue(params.channelSource) ??
    normalizeParamValue(params.channel_source) ??
    normalizeParamValue(params.source) ??
    normalizeParamValue(params.utm_source)
  );
}

const getConfiguredChannelSource = () => normalizeParamValue(import.meta.env.VITE_CHANNEL_SOURCE);

export function captureUtmParams(): void {
  const combined = getCombinedQueryString();
  if (!combined) return;

  const params = new URLSearchParams(combined);
  const captured: AttributionParams = {};

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = normalizeParamValue(params.get(key));
    if (value) {
      captured[key] = value;
    }
  });

  if (Object.keys(captured).length === 0) return;

  const capturedChannelSource = getAttributionChannelSourceFrom(captured);
  if (capturedChannelSource) {
    captured.channelSource = capturedChannelSource;
  }

  const merged = {
    ...getAttributionParams(),
    ...captured,
  };

  writeStorageObject(ATTRIBUTION_STORAGE_KEY, merged);

  const utm = pickUtmParams(merged);
  if (Object.keys(utm).length > 0) {
    writeStorageObject(UTM_STORAGE_KEY, utm);
  }

  console.log('[Attribution] Captured params:', merged);
}

export function getAttributionParams(): AttributionParams {
  const legacyUtm = readStorageObject(UTM_STORAGE_KEY);
  const storedAttribution = readStorageObject(ATTRIBUTION_STORAGE_KEY);

  return {
    ...legacyUtm,
    ...storedAttribution,
  };
}

export function getUtmParams(): Record<string, string> {
  return pickUtmParams(getAttributionParams());
}

export function getGoogleAdsClickParams(): Record<string, string> {
  return pickGoogleAdsClickParams(getAttributionParams());
}

export function getGoogleAdsContextParams(): Record<string, string> {
  return pickGoogleAdsContextParams(getAttributionParams());
}

export function getAttributionChannelSource(channelSource?: string): string | undefined {
  return (
    normalizeParamValue(channelSource) ??
    getAttributionChannelSourceFrom(getAttributionParams()) ??
    getConfiguredChannelSource()
  );
}

export function getAttributionPayload(channelSource?: string): Record<string, string> {
  const attribution = getAttributionParams();
  const resolvedChannelSource = getAttributionChannelSource(channelSource);
  const attributionRecord = {
    ...attribution,
    ...(resolvedChannelSource ? { channelSource: resolvedChannelSource } : {}),
  };
  const normalizedEntries = Object.entries(attributionRecord)
    .map(([key, value]) => [key, normalizeParamValue(value)] as const)
    .filter((entry): entry is readonly [string, string] => Boolean(entry[1]));

  if (normalizedEntries.length === 0) return {};

  const normalizedRecord = Object.fromEntries(normalizedEntries);

  return {
    ...normalizedRecord,
    attributionParams: JSON.stringify(normalizedRecord),
  };
}

export function getAttributionHeaders(channelSource?: string): Record<string, string> {
  const attribution = getAttributionParams();
  const headers: Record<string, string> = {};
  const utm = pickUtmParams(attribution);
  const clickParams = pickGoogleAdsClickParams(attribution);
  const contextParams = pickGoogleAdsContextParams(attribution);
  const attributionPayload = getAttributionPayload(channelSource);
  const resolvedChannelSource = attributionPayload.channelSource;

  Object.entries(utm).forEach(([key, value]) => {
    headers[key] = value;
  });

  if (Object.keys(utm).length > 0) {
    headers['X-UTM-Params'] = JSON.stringify(utm);
  }

  if (Object.keys(clickParams).length > 0) {
    headers['X-Google-Ads-Click-Params'] = JSON.stringify(clickParams);
  }

  if (Object.keys(contextParams).length > 0) {
    headers['X-Google-Ads-Context-Params'] = JSON.stringify(contextParams);
  }

  if (attributionPayload.attributionParams) {
    headers['X-Attribution-Params'] = attributionPayload.attributionParams;
  }

  if (resolvedChannelSource) {
    headers['Channel-Source'] = resolvedChannelSource;
  }

  return headers;
}

export function getUtmString(): string | undefined {
  const utm = getUtmParams();
  return Object.keys(utm).length > 0 ? JSON.stringify(utm) : undefined;
}
